import { useNavigate } from 'react-router-dom';
import {
  Box,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

// Define types for the component props
interface ImageCardItem {
  img: string;
  title: string;
  url: string;
  slug?: string;
  brand_page_link?: string | null; // ✅ ADDED - Brand's custom URL
}

interface ImageCardsProps {
  images: ImageCardItem[];
  hasBackgroundText: boolean;
  useInternalLinks?: boolean;
}

export default function ImageCards({
                                     images,
                                     hasBackgroundText,
                                     useInternalLinks = false
                                   }: ImageCardsProps) {
  const theme = useTheme();
  const navigate = useNavigate();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'));

  // Add debug logs
  console.log('ImageCards received images:', images);
  console.log('ImageCards images.length:', images.length);
  console.log('ImageCards hasBackgroundText:', hasBackgroundText);
  console.log('ImageCards useInternalLinks:', useInternalLinks);

  const handleCardClick = (item: ImageCardItem) => {
    // Priority 1: If there's a brand_page_link, use that (external)
    if (item.brand_page_link && item.brand_page_link.trim() !== '') {
      console.log('Using brand_page_link:', item.brand_page_link);
      window.location.href = item.brand_page_link;
      return;
    }

    // Priority 2: If useInternalLinks is true and we have a slug, use internal navigation
    if (useInternalLinks && item.slug) {
      console.log('Using internal navigation to:', `/internationals/brand/${item.slug}`);
      navigate(`/internationals/brand/${item.slug}`);
      return;
    }

    // Priority 3: Fall back to the original url (external)
    console.log('Using fallback url:', item.url);
    window.location.href = item.url;
  };

  return (
    <Box pb={10}>
      <ImageList cols={isLargeScreen ? 3 : 1} variant='standard' gap={10}>
        {images.map((item: ImageCardItem) => {
          console.log('Rendering image item:', item); // Debug each item

          return (
            <ImageListItem key={item.img} sx={{
              '&:hover img': {
                transform: 'scale(1.03)',
              },
              overflow: 'hidden',
            }}>
              <img
                src={`${item.img}?w=248&fit=crop&auto=format`}
                srcSet={`${item.img}?w=248&fit=crop&auto=format&dpr=2 2x`}
                alt={item.title}
                loading='lazy'
                style={{
                  transition: 'transform 0.5s ease',
                  width: '100%',
                  height: '436px'
                }}
                onError={(e) => {
                  console.error('Image failed to load:', item.img, e);
                }}
                onLoad={() => {
                  console.log('Image loaded successfully:', item.img);
                }}
              />
              {hasBackgroundText && (
                <ImageListItemBar
                  sx={{
                    background:
                      "linear-gradient(180deg, rgba(0, 0, 0, 0.00) 0%, rgba(0, 0, 0, 0.36) 100%)",
                    textAlign: 'center',
                    color: '#fff',
                    fontSize: '1rem',
                    textTransform: 'uppercase',
                    position: 'absolute',
                    top: 0,
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'end',
                    justifyContent: 'center',
                    cursor: 'pointer',
                  }}
                  onClick={() => handleCardClick(item)}
                  title={
                    <Typography
                      variant='h6'
                      sx={{
                        color: '#FFF',
                        fontSize: '40px',
                        fontWeight: '400',
                        lineHeight: '38px',
                        textTransform: 'capitalize',
                      }}
                    >
                      {item.title}
                    </Typography>
                  }
                />
              )}
            </ImageListItem>
          );
        })}
      </ImageList>
    </Box>
  );
}